import { GamesParameterHistoryService } from './games-parameter-history.service';
import { GameType } from '../models/games.model';
import { HistoricalTeamObj, IHSelectedMetric } from '../models/games-parameter.model';
import { Injectable } from '@angular/core';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';

@Injectable({
    providedIn: 'root'
})
export class GameHistoryDetailsServiceService {

    constructor(
        private gamesParameterHistoryService: GamesParameterHistoryService,
        private cvConfigurationService: CvConfigurationService
    ) { }

    getMetricDetails(
        ruleId,
        endDate: string
    ): IHSelectedMetric {
        const result = this.gamesParameterHistoryService.getMetrics(ruleId, endDate);
        return result.length > 0 ? result.find(x => !x.IsQualifier) : null;
    }

    getTopParticipants(
        metric: IHSelectedMetric,
        gameType: GameType,
        participants: any[],
        teamsInfo: { [key: number]: HistoricalTeamObj }
    ): { topParticipants: any[], participants: any[] } {
        if (!metric) return { topParticipants: [], participants: [] };

        if (gameType === GameType.TopDog) {
            const uniqueParticipants = participants.filter(item => item.rank !== undefined)
                .reduce((acc: any, item) => {
                    if (!acc.some(el => el.rank === item.rank)) acc.push(item);
                    return acc;
                }, []);

            const filteredParticipants = participants.reduce((acc: any, item) => {
                if (!acc.some(el => el.metricValue === item.metricValue)) acc.push(item);
                return acc;
            }, []);

            return {
                topParticipants: uniqueParticipants.sort((a, b) => a.rank - b.rank),
                participants: filteredParticipants
            };
        } else if (gameType === GameType.TopTeam) {
            const topParticipants = this.getMetricValuesTT(participants, metric, teamsInfo);
            return {
                topParticipants,
                participants: topParticipants
            };
        }
    }

    getMetricValuesTT(
        players,
        metric: IHSelectedMetric,
        teamsInfo: { [key: number]: HistoricalTeamObj }
    ) {
        const metricDir = metric.MetricDetail.DefaultMetricDirection;
        const result = players.reduce((acc: Record<string, { metricValue: string, rank: number | null }>, player) => {
            const team = player.participant.Team;
            const { rank } = player;
            const teamObj = teamsInfo[player.participant.TeamID];
            const metricsValues = teamObj?.metricsValues;
            const metric = Array.isArray(metricsValues) && metricsValues.length > 0
                ? metricsValues[0]?.MetricDisplayValue
                : null;
            if (!acc[team]) {
                acc[team] = { metricValue: metric, rank: rank ?? null };
            }

            return acc;
        }, {});

        let playerList = Object.entries(result).map(([team, data]) => ({
            team,
            metricValue: data["metricValue"],
            metric: data["metricValue"],
            rank: data["rank"]
        }));

        playerList = playerList.sort((a, b) => {
            if (a.rank === null && b.rank === null) {
                return metricDir == 1 ? (b.metricValue - a.metricValue) : (a.metricValue - b.metricValue);
            } else if (a.rank == null) {
                return 1;
            } else if (b.rank == null) {
                return -1;
            }

            if (a.rank !== b.rank) {
                return a.rank - b.rank;
            }

            return metricDir ? (b.metricValue - a.metricValue) : (a.metricValue - b.metricValue);
        });

        return playerList;
    }

    getLocalizedTierText(
        tier: number,
        metric,
        topParticipants,
        participants,
        getLocalizedText: (key: string, defaultText: string) => string
    ): string {
        if (!metric || !metric.MetricDetail) return '---';

        const allRankNull = topParticipants.every(item => item.rank === null);
        let metricValue = null;

        if (topParticipants.length > 0 && !allRankNull) {
            metricValue = topParticipants.find(x => x.rank == tier)?.metric ?? '---';
        } else {
            metricValue = participants[tier - 1]?.metric ?? '---';
        }

        let hasDash = '-';
        if (this.cvConfigurationService.isCxOne) {
            hasDash = metricValue !== '---' ? '-' : '';
        }

        switch (tier) {
            case 1:
                return `${getLocalizedText("First_Place", "1st Place")} ${hasDash} ${metricValue}`;
            case 2:
                return `${getLocalizedText("Second_Place", "2nd Place")} ${hasDash} ${metricValue}`;
            case 3:
                return `${getLocalizedText("Third_Place", "3rd Place")} ${hasDash} ${metricValue}`;
            default:
                let number = tier + getLocalizedText("Th_Symbol", "th") + " ";
                number += `${getLocalizedText("Place", "Place")} ${hasDash} ${metricValue}`;
                return number;
        }
    }

    getRangeText(
        reward,
        getLocalizedText: (key: string, defaultText: string) => string,
        hasConversion: boolean = false
    ): string {
        if (!reward?.metricTarget) return '';

        const comparerType = reward.metricTarget.ComparerType;
        // Handle null leftNumber specifically for "Less Than" case
        let leftNumber = reward.metricTarget.LeftNumber;
        let rightNumber = reward.metricTarget.RightNumber;

        // For "Less Than" (comparerType: 2), if leftNumber is null, use rightNumber as the threshold
        if (comparerType === 2 && (leftNumber === null || leftNumber === undefined)) {
            leftNumber = rightNumber;
        }

        // For "Between" ranges with down direction metrics, swap values for proper display order
        if (comparerType === 3 && reward.metricTarget?.MetricDetail?.DefaultMetricDirection === 0) {
            [leftNumber, rightNumber] = [rightNumber, leftNumber];
        }


        const formattedLeft = hasConversion && leftNumber !== null && leftNumber !== undefined
            ? this.getFormattedData(reward.metricTarget?.MetricDetail?.ConversionType ?? 0, leftNumber)
            : leftNumber;
        const formattedRight = hasConversion && rightNumber !== null && rightNumber !== undefined
            ? this.getFormattedData(reward.metricTarget?.MetricDetail?.ConversionType ?? 0, rightNumber)
            : rightNumber;

        switch (comparerType) {
            case 1:
                return `${getLocalizedText("Greater_Than", "Greater Than")} ${formattedLeft ?? ''}`;
            case 2:
                return `${getLocalizedText("Less_Than", "Less Than")} ${formattedLeft ?? ''}`;
            case 3:
                return `${getLocalizedText("Between", "Between").replace("{0}", formattedLeft?.toString() ?? '').replace("{1}", formattedRight?.toString() ?? '')}`;
            default:
                return '';
        }
    }

    getConnectGamesText(
        isConnect3: boolean,
        getLocalizedText: (key: string, defaultText: string) => string
    ): string {
        return isConnect3
            ? getLocalizedText("Three_In_Row", "3 in a row")
            : getLocalizedText("Blackout", "Connect All")
    }

    getLocalizedMultiplierMadnessText(
        metric: IHSelectedMetric,
        isMetricTitle: boolean,
        getLocalizedText: (key: string, defaultText: string) => string
    ): string {
        if (isMetricTitle) {
            return getLocalizedText("Rewards_Per", "Rewards Per {0}")
                .replace("{0}", metric.MetricDetail?.Name ?? '');
        }
        return getLocalizedText("Max_Payout_Possible", "Max Payout Possible");
    }

    getExtraDetailsText(
        levelID: string,
        gameType: GameType,
        extraRewardDetails: Map<number, number>,
        getLocalizedText: (key: string, defaultText: string) => string
    ): string {
        const level = +levelID;
        const count = extraRewardDetails.get(level);
        switch (gameType) {
            case GameType.Bullseye:
                if (count == 0 || count == undefined) return getLocalizedText("Not_Hit", "Not Hit");
                if (count == 1) return getLocalizedText("Hit_Once", "Hit once");
                return getLocalizedText("Hit_N_Times", "Hit {0} times").replace("{0}", count.toString());
            case GameType.Milestone:
                if (count == 0 || count == undefined) return getLocalizedText("Not_Reached", "Not Reached");
                if (count == 1) return getLocalizedText("Reached_Once", "Reached once");
                return getLocalizedText("Reached_N_Times", "Reached {0} times").replace("{0}", count.toString());
        }
    }

    getRafflePrizeText(
        index: number,
        getLocalizedText: (key: string, defaultText: string) => string
    ) {
        return `${getLocalizedText("Prize", "Prize")} ${index}`;
    }

    getBracketRoundName(
        round: number,
        getLocalizedText: (key: string, defaultText: string) => string
    ) {
        switch (round) {
            case 1:
                return getLocalizedText("Round_1_Final", "Final");
            case 2:
                return getLocalizedText("Round_2_Semi_Finals", "Semi Finals");
            case 3:
                return getLocalizedText("Round_3_Quarter", "Quarters");
            case 4:
                return getLocalizedText("Round_4_Octaves", "Octaves");
        }
    }

    getFormattedData(
        conversionType: number,
        value: number
    ): any {
        let formatted: any;
        value ??= 0;

        switch (conversionType) {
            case 1: //Second
                formatted = value;
                break;
            case 2: //Minute
                formatted = value / 60;
                break;
            case 3: //Hour
                formatted = Math.floor((value / 3600) * 100) / 100;
                break;
            case 4: //Time
                formatted = this.formatMetricValueToDate(value);
                break;
            case 5: //Percent
                formatted = `${(value * 100).toFixed(2)}%`;
                break;
            default:
                formatted = value;
                break;
        }
        return formatted;
    }

    formatMetricValueToDate(metricValue: number): string {
        const hours = parseInt(Math.floor(metricValue / 3600).toString());
        const minutes = parseInt(Math.floor((metricValue - (hours * 3600)) / 60).toString());
        const seconds = parseInt((metricValue - (hours * 3600) - (minutes * 60)).toString());

        const hh = (hours < 10) ? "0" + hours : hours;
        const mm = (minutes < 10) ? "0" + minutes : minutes;
        const ss = (seconds < 10) ? "0" + seconds : seconds;

        return `${hh}:${mm}:${ss}`;
    }
}
